import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { PaletteMode, useMediaQuery } from '@mui/material';
import brand from '../theme/brand';

interface ThemeContextType {
  mode: PaletteMode;
  toggleColorMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [override, setOverride] = useState<PaletteMode | null>(null);
  const mode: PaletteMode = override ?? (prefersDark ? 'dark' : 'light');

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setOverride(mode === 'light' ? 'dark' : 'light');
      },
    }),
    [mode]
  );

  const theme = useMemo(() => {
    const light = mode === 'light';

    return createTheme({
      palette: {
        mode,
        primary: light
          ? { main: brand.forest, light: brand.sage, dark: brand.forestDeep, contrastText: brand.cream }
          : { main: brand.sageLight, light: '#9DBFAC', dark: brand.sage, contrastText: brand.forestDeep },
        secondary: {
          main: brand.copper,
          light: brand.copperLight,
          dark: '#A96438',
          contrastText: '#FFFFFF',
        },
        background: light
          ? { default: brand.cream, paper: '#FFFFFF' }
          : { default: brand.darkBg, paper: brand.darkPaper },
        text: light
          ? { primary: brand.ink, secondary: brand.warmGray }
          : { primary: brand.cream, secondary: brand.darkTextSecondary },
        divider: light ? brand.border : brand.darkBorder,
        success: { main: '#3E7D5C' },
        info: { main: brand.sage },
      },
      shape: {
        borderRadius: 12,
      },
      typography: {
        fontFamily: brand.sans,
        h1: { fontFamily: brand.serif, letterSpacing: '-0.02em' },
        h2: { fontFamily: brand.serif, letterSpacing: '-0.02em' },
        h3: { fontFamily: brand.serif, letterSpacing: '-0.02em' },
        h4: { fontFamily: brand.serif, letterSpacing: '-0.01em' },
        h5: { fontFamily: brand.serif, letterSpacing: '-0.01em' },
        h6: { fontFamily: brand.sans, fontWeight: 700, letterSpacing: '-0.01em' },
        subtitle1: { fontWeight: 600 },
        subtitle2: { fontWeight: 600 },
        button: { fontWeight: 600, textTransform: 'none' },
        overline: { fontWeight: 700, letterSpacing: '0.12em' },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: light ? brand.cream : brand.darkBg,
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: light ? brand.forest : brand.darkPaper,
              color: brand.cream,
              backgroundImage: 'none',
              boxShadow: 'none',
              borderBottom: `1px solid ${light ? 'rgba(245,240,232,0.08)' : brand.darkBorder}`,
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 10,
              boxShadow: 'none',
            },
            containedPrimary: {
              '&:hover': {
                boxShadow: light
                  ? '0 6px 20px rgba(27, 58, 45, 0.25)'
                  : '0 6px 20px rgba(0, 0, 0, 0.4)',
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 16,
              border: `1px solid ${light ? brand.border : brand.darkBorder}`,
              boxShadow: light ? brand.softShadow : '0 4px 24px rgba(0,0,0,0.35)',
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              backgroundColor: light ? '#FFFFFF' : brand.darkPaperRaised,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: light ? brand.border : brand.darkBorder,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: light ? brand.sage : brand.sageLight,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: light ? brand.sage : brand.sageLight,
                borderWidth: 1.5,
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              fontWeight: 600,
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              fontWeight: 600,
              textTransform: 'none',
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              backgroundColor: light ? brand.forest : brand.darkPaperRaised,
              fontSize: '0.75rem',
              borderRadius: 8,
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              backgroundImage: 'none',
            },
          },
        },
      },
    });
  }, [mode]);

  return (
    <ThemeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
